import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { BanIcon, CheckIcon } from 'lucide-react';

import { cn } from '#/lib/utils';
import PermissionMatrix from '#/modules/infra/settings/access/components/PermissionMatrix';
import { buildPermissionSections, needsRead } from '#/modules/infra/settings/access/helper/permissionCatalog';

export const EFFECT_ALLOW = 'ALLOW';
export const EFFECT_DENY = 'DENY';

function isGranted(perm, roleGrantedIds, overrides) {
  const effect = overrides[perm.id];
  if (effect === EFFECT_DENY) {
    return false;
  }
  return effect === EFFECT_ALLOW || roleGrantedIds.has(perm.id);
}

/** 點擊循環：沿用角色 → 額外允許 → 拒絕；角色已給的權限略過「額外允許」 */
function nextEffect(current, fromRole) {
  if (!current) {
    return fromRole ? EFFECT_DENY : EFFECT_ALLOW;
  }
  if (current === EFFECT_ALLOW) {
    return EFFECT_DENY;
  }
  return null;
}

/** 外觀沿用共用 Checkbox：角色已給＝勾選樣式（primary 底白勾），額外允許／拒絕換底色區分 */
const CELL_BASE_CLASS = cn(
  'relative flex size-4 shrink-0 items-center justify-center rounded-[4px] border bg-panel transition-colors',
  'outline-none hover:border-primary-500 hover:ring-1 hover:ring-primary-500/35',
  'focus-visible:border-primary-500 focus-visible:ring-1 focus-visible:ring-primary-500/35',
  'disabled:cursor-not-allowed disabled:opacity-50 [&>svg]:size-3.5',
);

const STATE_STYLES = {
  role: 'border-primary-500 bg-primary-500 text-white',
  allow: 'border-emerald-500 bg-emerald-500 text-white',
  deny: 'border-rose-500 bg-rose-500 text-white',
  none: 'border-gray-400',
};

function StateIcon({ state }) {
  if (state === 'deny') {
    return <BanIcon aria-hidden />;
  }
  return state === 'none' ? null : <CheckIcon aria-hidden />;
}

StateIcon.propTypes = {
  state: PropTypes.oneOf(['role', 'allow', 'deny', 'none']).isRequired,
};

function CellButton({ state, label, disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(CELL_BASE_CLASS, STATE_STYLES[state])}
    >
      <StateIcon state={state} />
    </button>
  );
}

CellButton.propTypes = {
  state: PropTypes.oneOf(['role', 'allow', 'deny', 'none']).isRequired,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

const STATE_TEXT = {
  role: '角色已給',
  allow: '額外允許',
  deny: '拒絕',
  none: '未給',
};

/**
 * 帳號權限：角色帶入的權限＋逐項覆寫（允許／拒絕）。overrides = { [permissionId]: 'ALLOW'|'DENY' }。
 */
const UserPermissionOverrideEditor = ({
  catalog,
  roleGrantedIds,
  overrides,
  onChange,
  disabled = false,
}) => {
  const sections = useMemo(() => buildPermissionSections(catalog), [catalog]);

  function resolveState(perm) {
    const effect = overrides[perm.id];
    if (effect === EFFECT_DENY) {
      return 'deny';
    }
    if (effect === EFFECT_ALLOW) {
      return 'allow';
    }
    return roleGrantedIds.has(perm.id) ? 'role' : 'none';
  }

  function handleClick(perm, row) {
    const next = { ...overrides };
    const effect = nextEffect(overrides[perm.id], roleGrantedIds.has(perm.id));
    if (effect) {
      next[perm.id] = effect;
    } else {
      delete next[perm.id];
    }

    const read = row.actions.read;
    const nowGranted = isGranted(perm, roleGrantedIds, next);
    if (nowGranted && needsRead(perm) && read && !isGranted(read, roleGrantedIds, next)) {
      if (roleGrantedIds.has(read.id)) {
        delete next[read.id];
      } else {
        next[read.id] = EFFECT_ALLOW;
      }
    }

    // 讀取變成沒有 → 同列需要讀取的動作也收回（額外允許拿掉；角色給的改成拒絕）
    if (perm.action === 'read' && !nowGranted) {
      row.perms.filter(needsRead).forEach((other) => {
        if (!isGranted(other, roleGrantedIds, next)) {
          return;
        }
        if (roleGrantedIds.has(other.id)) {
          next[other.id] = EFFECT_DENY;
        } else {
          delete next[other.id];
        }
      });
    }
    onChange(next);
  }

  if (!sections.length) {
    return <p className="text-sm text-muted-foreground">沒有可設定的權限（權限目錄可能尚未建立）。</p>;
  }

  return (
    <PermissionMatrix
      sections={sections}
      countGranted={(perms) => perms.filter((perm) => isGranted(perm, roleGrantedIds, overrides)).length}
      legend={(
        <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
          {['role', 'allow', 'deny', 'none'].map((state) => (
            <span key={state} className="inline-flex items-center gap-1.5">
              <span className={cn(CELL_BASE_CLASS, 'hover:ring-0', STATE_STYLES[state])} aria-hidden>
                <StateIcon state={state} />
              </span>
              {STATE_TEXT[state]}
            </span>
          ))}
          <span className="text-gray-500">點一下切換：未給 → 額外允許 → 拒絕 → 還原</span>
        </p>
      )}
      renderCell={(perm, row) => {
        const state = resolveState(perm);
        return (
          <CellButton
            state={state}
            label={`${perm.name || perm.code}：${STATE_TEXT[state]}`}
            disabled={disabled}
            onClick={() => handleClick(perm, row)}
          />
        );
      }}
    />
  );
};

UserPermissionOverrideEditor.propTypes = {
  catalog: PropTypes.array.isRequired,
  roleGrantedIds: PropTypes.instanceOf(Set).isRequired,
  overrides: PropTypes.objectOf(PropTypes.oneOf([EFFECT_ALLOW, EFFECT_DENY])).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default UserPermissionOverrideEditor;
